'use client';

import * as React from 'react';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from './utils';

// Context for z-index management
const TooltipContext = React.createContext<{ zIndex: number }>({ zIndex: 30 });

// Context for mobile detection - apps should provide this via TooltipMobileProvider
const TooltipMobileContext = React.createContext<boolean>(false);
export const TooltipMobileProvider = TooltipMobileContext.Provider;
export const useTooltipMobile = () => React.useContext(TooltipMobileContext);

export const useTooltipContext = () => React.useContext(TooltipContext);
export const TooltipContextProvider = TooltipContext.Provider;

export type BaseTooltipProps = {
  children: React.ReactNode;
  content: React.ReactNode;
  className?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
  alignOffset?: number;
  initialOpen?: boolean;
  forceOpen?: boolean;
  enabled?: boolean;
  onOutsideClicked?: () => void;
  onTooltipContentClick?: () => void;
  zIndex?: number;
  collisionPadding?: number | { top?: number; right?: number; bottom?: number; left?: number };
  hideArrow?: boolean;
  arrowClassName?: string;
  /** Override mobile detection from context */
  isMobile?: boolean;
};

/**
 * BaseTooltip - A higher-level tooltip component with mobile touch support.
 *
 * IMPORTANT: Child components must support ref forwarding via React.forwardRef.
 * If using a custom component as the trigger, ensure it forwards its ref to a DOM element.
 *
 * @example
 * // Works - native elements forward refs automatically
 * <BaseTooltip content="Hello"><button>Click me</button></BaseTooltip>
 *
 * // Works - component uses forwardRef
 * const MyButton = React.forwardRef((props, ref) => <button ref={ref} {...props} />);
 * <BaseTooltip content="Hello"><MyButton>Click me</MyButton></BaseTooltip>
 *
 * // DOES NOT WORK - component doesn't forward ref
 * const BadButton = (props) => <button {...props} />;
 * <BaseTooltip content="Hello"><BadButton>Click me</BadButton></BaseTooltip>
 *
 * See: https://github.com/radix-ui/primitives/issues/1240
 */
export function BaseTooltip({
  children,
  content,
  className,
  side = 'bottom',
  sideOffset = 8,
  align,
  alignOffset,
  initialOpen = false,
  forceOpen,
  enabled = true,
  onOutsideClicked,
  onTooltipContentClick,
  zIndex,
  collisionPadding,
  hideArrow = false,
  arrowClassName,
  isMobile: isMobileProp,
}: BaseTooltipProps) {
  // SSR-safe: default to false during SSR, will update on client hydration
  // Using a consistent default prevents hydration mismatch
  const [hasMounted, setHasMounted] = React.useState(false);
  const isMobileFromContext = useTooltipMobile();
  const isMobile = isMobileProp ?? isMobileFromContext;

  // State machine for tooltip visibility
  const [isInitialOpened, setIsInitialOpened] = React.useState(!initialOpen);
  const [clickedOutside, setClickedOutside] = React.useState(!initialOpen);
  const [clickedManually, setClickedManually] = React.useState(false);

  const contentRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLElement>(null);

  const { zIndex: contextZIndex } = useTooltipContext();
  const effectiveZIndex = zIndex ?? contextZIndex;
  const effectiveCollisionPadding = collisionPadding ?? (isMobile ? 16 : 0);

  // SSR hydration safety: mark as mounted after first render
  React.useEffect(() => {
    setHasMounted(true);
  }, []);

  // Calculate open state
  const getShouldShowTooltip = React.useCallback((): boolean | undefined => {
    if (!enabled) return false;
    if (forceOpen) return true;

    if (isMobile) {
      // Mobile: show if initial hasn't been consumed, or if manually clicked
      if (!isInitialOpened && initialOpen) {
        return true;
      }
      return clickedManually && !clickedOutside;
    }

    // Desktop: show if initial, or if not clicked outside (let Radix handle hover)
    if (!isInitialOpened && initialOpen) {
      return true;
    } else {
      // After initial is consumed, return undefined to let Radix handle hover
      // Unless user clicked outside, which resets to default hover behavior
      return clickedOutside ? undefined : initialOpen ? true : undefined;
    }
  }, [enabled, forceOpen, isMobile, isInitialOpened, initialOpen, clickedManually, clickedOutside]);

  const shouldShowTooltip = getShouldShowTooltip();

  // Outside click detection
  React.useEffect(() => {
    if (!isMobile) return;
    // Only attach listener when we need to detect outside clicks
    if (shouldShowTooltip !== true) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (contentRef.current?.contains(target) || triggerRef.current?.contains(target)) {
        return;
      }
      setClickedOutside(true);
      setIsInitialOpened(true); // Mark initial as consumed
      onOutsideClicked?.();
    };

    window.addEventListener('touchstart', handleOutsideClick);
    window.addEventListener('mousedown', handleOutsideClick);

    return () => {
      window.removeEventListener('touchstart', handleOutsideClick);
      window.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isMobile, shouldShowTooltip, onOutsideClicked]);

  const handleTriggerClick = () => {
    if (isMobile) {
      if (clickedManually && !clickedOutside) {
        // Second click on mobile = close
        setClickedOutside(true);
      } else {
        // First click or reopening after outside click
        setClickedManually(true);
        setClickedOutside(false);
        setIsInitialOpened(true); // Consume initial state
      }
    } else {
      // Desktop: clicking trigger consumes initial state
      setIsInitialOpened(true);
    }
  };

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <TooltipPrimitive.Provider delayDuration={0}>
      <TooltipPrimitive.Root open={shouldShowTooltip} disableHoverableContent={true}>
        <TooltipPrimitive.Trigger asChild onClick={handleTriggerClick} ref={triggerRef as React.RefObject<HTMLButtonElement>}>
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            ref={contentRef}
            side={side}
            sideOffset={sideOffset}
            align={align}
            alignOffset={alignOffset}
            collisionPadding={effectiveCollisionPadding}
            onClick={onTooltipContentClick}
            onPointerDownOutside={() => {
              setIsInitialOpened(true);
              setClickedOutside(true);
              setClickedManually(false);
              onOutsideClicked?.();
            }}
            className={cn(
              'z-50 overflow-hidden rounded-sm bg-background-inverse px-2 py-1 text-xs text-text-inverse',
              'animate-in fade-in-0 zoom-in-95',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
              'data-[side=bottom]:slide-in-from-top-2',
              'data-[side=left]:slide-in-from-right-2',
              'data-[side=right]:slide-in-from-left-2',
              'data-[side=top]:slide-in-from-bottom-2',
              className,
            )}
            style={{ zIndex: effectiveZIndex }}
          >
            <div className="text-popover-foreground">{content}</div>
            {!hideArrow && (
              <TooltipPrimitive.Arrow
                className={cn(
                  'bg-background-inverse fill-background-inverse z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]',
                  arrowClassName,
                )}
              />
            )}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
